import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { formatEuro, formatDate } from "../lib/format";
import { STATO_META } from "../lib/proforma";
import ProformaDialog from "./ProformaDialog";
import ProformaPreviewDialog from "./ProformaPreviewDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { FileText, Plus, Loader2, Eye, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function ProformaListDialog({ open, onOpenChange, pratica, client, clienteKey }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [studio, setStudio] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);

  const load = async () => {
    if (!pratica?.id) return;
    setLoading(true);
    try {
      const [{ data }, me] = await Promise.all([
        api.get(`/studio/pratiche/${pratica.id}/proforme${clienteKey ? `?cliente_key=${encodeURIComponent(clienteKey)}` : ""}`),
        api.get("/studio/me"),
      ]);
      setRows(data || []);
      setStudio(me.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, pratica?.id, clienteKey]);

  const del = async (f) => {
    if (!window.confirm(`Eliminare la proforma n. ${f.numero}?`)) return;
    try {
      if (f.pratica_id && f.allegato_id) { try { await api.delete(`/studio/pratiche/${f.pratica_id}/allegati-files/${f.allegato_id}`); } catch { /* allegato assente */ } }
      await api.delete(`/studio/proforme/${f.id}`);
      setRows((x) => x.filter((i) => i.id !== f.id));
      toast.success("Proforma eliminata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="pgp-modal bg-[#121824] border-slate-700 text-slate-100 max-w-3xl overflow-y-auto" data-testid="proforma-list-dialog">
          <DialogHeader>
            <DialogTitle className="font-head flex items-center gap-2">
              <FileText className="h-5 w-5 text-teal-400" /> Fatture Proforma
              {pratica?.numero_pratica && <span className="text-sm font-normal text-slate-500">· {pratica.numero_pratica}</span>}
            </DialogTitle>
          </DialogHeader>

          <div className="flex justify-end">
            <Button onClick={() => { setEditing(null); setCreateOpen(true); }} data-testid="proforma-list-new" className="bg-teal-500 hover:bg-teal-600 text-[#0a0d14] font-semibold gap-1"><Plus className="h-4 w-4" /> Nuova Fattura Proforma</Button>
          </div>

          <div className="mt-2">
            {loading ? (
              <div className="p-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-teal-400" /></div>
            ) : rows.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-400" data-testid="proforma-list-empty">Nessuna fattura proforma per questa pratica.</div>
            ) : (
              <div className="divide-y divide-slate-800 rounded-lg border border-slate-800">
                {rows.map((f) => {
                  const sm = STATO_META[f.stato] || STATO_META.bozza;
                  return (
                    <div key={f.id} className="flex items-center gap-3 px-3 py-2" data-testid={`proforma-list-row-${f.id}`}>
                      <div className="font-mono text-teal-400 text-sm shrink-0">{f.numero}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-100 truncate">{f.intestatario || "—"}</div>
                        <div className="text-[11px] text-slate-500 font-mono">{formatDate(f.data)}</div>
                      </div>
                      <div className="font-mono text-emerald-400 text-sm shrink-0">{formatEuro(f.totale_netto)}</div>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full border ${sm.cls} shrink-0`}>{sm.label}</span>
                      <div className="flex items-center gap-2 shrink-0">
                        <button onClick={() => setPreview(f)} data-testid={`proforma-list-view-${f.id}`} title="Apri / Anteprima PDF" className="text-slate-400 hover:text-teal-400"><Eye className="h-4 w-4" /></button>
                        <button onClick={() => { setEditing(f); setCreateOpen(true); }} data-testid={`proforma-list-edit-${f.id}`} title="Modifica" className="text-slate-400 hover:text-amber-400"><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => del(f)} data-testid={`proforma-list-del-${f.id}`} title="Elimina" className="text-red-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <ProformaDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        pratica={pratica}
        client={editing ? null : client}
        clienteKey={clienteKey}
        editing={editing}
        onSaved={(created) => { load(); if (created) setPreview(created); }}
      />
      <ProformaPreviewDialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)} proforma={preview} studio={studio} />
    </>
  );
}
